import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../components/Icon';
import type { TreatmentStage } from '../../../types/admin';
import { CycleSettingsForm } from './ModelingPanel/CycleSettingsForm';
import { VisualizationMediaSection } from './ModelingPanel/VisualizationMediaSection';
import { PlanOptionsSection } from './PlanOptionsSection';
import { planTitleMap } from './plan-options/pricing';
import styles from './StepPanel.module.scss';

type ModelingTab = 'plans' | 'settings' | 'media';

const TAB_ORDER: readonly ModelingTab[] = ['plans', 'settings', 'media'];

type ModelingPanelProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
};

/**
 * The Моделювання step: plans first, everything else one click away.
 *
 * The three areas (plans, cycle-wide settings, visualization media) used to be
 * stacked in one column, which meant scrolling past two forms and a media grid
 * to reach the alternatives. They are tabs now; a tab is mounted on first visit
 * and then kept mounted, so half-typed edits survive switching back and forth.
 */
export function ModelingPanel({ patientId, treatmentId, stage }: ModelingPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ModelingTab>('plans');
  const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<ModelingTab>>(
    () => new Set<ModelingTab>(['plans']),
  );
  const [isSettingsDirty, setSettingsDirty] = useState(false);
  const tabRefs = useRef(new Map<ModelingTab, HTMLButtonElement | null>());

  const planOptions = useMemo(() => stage.plan_options ?? [], [stage.plan_options]);
  const mediaCount = stage.visualization_media?.length ?? 0;

  const titles = useMemo(
    () =>
      planTitleMap(planOptions, {
        main: t('planOptions.mainPlanTitle'),
        alternative: (index: number) => t('planOptions.alternativePlanTitle', { index }),
      }),
    [planOptions, t],
  );
  const selectedPlan = planOptions.find((option) => option.is_selected) ?? null;

  const planApprovedAt = stage.plan_approved_at ?? null;
  const planApprovedLabel = useMemo(() => {
    if (!planApprovedAt) return null;
    try {
      return new Date(planApprovedAt).toLocaleString();
    } catch {
      return planApprovedAt;
    }
  }, [planApprovedAt]);

  const openTab = useCallback((tab: ModelingTab) => {
    setActiveTab(tab);
    setVisitedTabs((previous) => {
      if (previous.has(tab)) return previous;
      const next = new Set(previous);
      next.add(tab);
      return next;
    });
  }, []);

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const lastIndex = TAB_ORDER.length - 1;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = TAB_ORDER[nextIndex];
    openTab(nextTab);
    tabRefs.current.get(nextTab)?.focus();
  }

  const tabLabels: Record<ModelingTab, string> = {
    plans: t('modeling.tabPlans'),
    settings: t('modeling.tabSettings'),
    media: t('modeling.tabMedia'),
  };
  const tabCounts: Record<ModelingTab, number | null> = {
    plans: planOptions.length,
    settings: null,
    media: mediaCount,
  };

  return (
    <div className={styles.panel}>
      <div className={styles.modelingStatusBar}>
        {planApprovedAt ? (
          <span
            className={`${styles.statusStrip} ${styles.statusStripDone}`}
            role="status"
            aria-live="polite"
            data-testid="plan-approved-banner"
          >
            <Icon name="check" size={16} />
            {t('modeling.planApprovedByPatient')}
            {planApprovedLabel && (
              <time dateTime={planApprovedAt}>
                {t('modeling.planApprovedAt')}: {planApprovedLabel}
              </time>
            )}
          </span>
        ) : (
          <span className={styles.statusStrip} role="status" data-testid="plan-pending-banner">
            <Icon name="info" size={16} />
            {t('modeling.planPendingApproval')}
          </span>
        )}
        <span className={styles.statusStripSelection} data-testid="modeling-selected-plan">
          {selectedPlan
            ? `${t('modeling.selectedPlanLabel')}: ${titles.get(selectedPlan.id) ?? ''}`
            : t('modeling.noSelectedPlan')}
        </span>
      </div>

      <div className={styles.subTabBar} role="tablist" aria-label={t('modeling.tabsLabel')}>
        {TAB_ORDER.map((tab, index) => (
          <button
            key={tab}
            type="button"
            ref={(node) => {
              tabRefs.current.set(tab, node);
            }}
            id={`${stage.id}-modeling-${tab}-tab`}
            role="tab"
            aria-selected={activeTab === tab}
            aria-controls={`${stage.id}-modeling-${tab}-panel`}
            tabIndex={activeTab === tab ? 0 : -1}
            className={`${styles.subTab} ${activeTab === tab ? styles.subTabActive : ''}`}
            onClick={() => openTab(tab)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            <span>{tabLabels[tab]}</span>
            {tabCounts[tab] !== null && (
              <span className={styles.subTabCount}>{tabCounts[tab]}</span>
            )}
            {/* Decorative: the form itself announces the unsaved state. */}
            {tab === 'settings' && isSettingsDirty && (
              <span
                className={styles.subTabDot}
                title={t('modeling.unsavedChanges')}
                aria-hidden="true"
                data-testid="settings-tab-dirty"
              />
            )}
          </button>
        ))}
      </div>

      {visitedTabs.has('plans') && (
        <div
          id={`${stage.id}-modeling-plans-panel`}
          role="tabpanel"
          aria-labelledby={`${stage.id}-modeling-plans-tab`}
          hidden={activeTab !== 'plans'}
        >
          <PlanOptionsSection patientId={patientId} treatmentId={treatmentId} stage={stage} />
        </div>
      )}

      {visitedTabs.has('settings') && (
        <div
          id={`${stage.id}-modeling-settings-panel`}
          role="tabpanel"
          aria-labelledby={`${stage.id}-modeling-settings-tab`}
          hidden={activeTab !== 'settings'}
        >
          <CycleSettingsForm
            patientId={patientId}
            treatmentId={treatmentId}
            stage={stage}
            onDirtyChange={setSettingsDirty}
          />
        </div>
      )}

      {visitedTabs.has('media') && (
        <div
          id={`${stage.id}-modeling-media-panel`}
          role="tabpanel"
          aria-labelledby={`${stage.id}-modeling-media-tab`}
          hidden={activeTab !== 'media'}
        >
          <VisualizationMediaSection
            patientId={patientId}
            treatmentId={treatmentId}
            stage={stage}
          />
        </div>
      )}
    </div>
  );
}
